import { dateAdd, DASHBOARD_TILES, Employee, EmployeeStatus } from "@pentacode/core";
import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./dashboard-tile";
import "../employee-absence-list";
import { DateString } from "@pentacode/openapi";
import { app } from "../../init";
import { Task } from "@lit/task";
import { shared } from "../../styles";
import { DayMonthFormatter, getStartOfToday, isoToZoned, userFormatSettings } from "../../utils/temporal";
import "../event-list";

type Birthday = {
    date: Temporal.PlainDateTime;
    data: Employee;
};

@customElement("ptc-dashboard-tile-birthdays")
export class DashboardTileBirthdays extends LitElement {
    @property({ type: Array })
    days: [past: number, future: number] = [0, 7];

    @property({ type: String })
    mode: "detailed" | "compact" = "detailed";

    @state()
    from: DateString = new Date().toISOString() as DateString;

    @state()
    to: DateString = dateAdd(new Date().toISOString() as DateString, {
        years: 1,
    }) as DateString;

    private _birthdayTask = new Task(this, {
        task: async () => {
            const currentYear = Temporal.Now.plainDateISO(userFormatSettings.timeZone).year;

            return app.employees
                .filter((e) => e.birthday && e.status === EmployeeStatus.Active)
                .flatMap((employee) => {
                    const birthdays = getBirthdayDatesForYears(employee.birthday, currentYear);
                    return Object.values(birthdays).map((birthday) => ({
                        date: birthday,
                        data: employee,
                    }));
                });
        },
        args: () => [],
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }
        `,
    ];

    private _birthdayEntryTemplate = (event: Birthday): TemplateResult => html`
        <ptc-avatar .employee=${event.data}></ptc-avatar>
        <span class="semibold">${event.data.name}</span>
    `;

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--purple)"
                label=${DASHBOARD_TILES.birthdays}
                icon="birthday-cake"
                .size=${{ inline: 1, block: 2 }}
            >
                ${this._birthdayTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-spinner
                            class="place-self-center double-padded"
                            active
                            style="font-size: 1rem"
                        ></ptc-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Geburtstage konnten nicht geladen werden!</div>`,

                    complete: (birthdays) => {
                        if (!birthdays.length) {
                            return html`<div class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">🔍</span>
                                <p>Leider keine Geburtstage in Sicht.</p>
                            </div>`;
                        }

                        return html` <ptc-event-list
                            .timeWindow=${[
                                getStartOfToday().subtract({ days: this.days[0] }),
                                getStartOfToday().add({ days: this.days[1] }),
                            ]}
                            .dateFormat=${DayMonthFormatter}
                            mode="detailed"
                            .events="${birthdays}"
                            .itemTemplate=${this._birthdayEntryTemplate}
                        >
                            <div slot="empty-message">
                                Niemand hat in den ${this.days[1] > 0 ? `nächsten ${this.days[1]}` : ""}
                                ${this.days[0] > 0 ? `${this.days[1] > 0 ? "oder" : ""} letzten ${this.days[0]}` : ""}
                                Tagen Geburtstag.
                            </div>
                        </ptc-event-list>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-birthdays": DashboardTileBirthdays;
    }
}

/**
 * Generates birthday dates for the given year, as well as the previous and next year,
 * based on the employee's birthday (month and day).
 *
 * @param {string} employeeBirthday - The ISO string representing the employee's birthday.
 * @param {number} year - The reference year for which to generate the birthday.
 * @returns {{
 *   currentYearBirthday: Temporal.PlainDateTime,
 *   nextYearBirthday: Temporal.PlainDateTime,
 *   prevYearBirthday: Temporal.PlainDateTime
 * }} An object containing the birthday dates for the current, next, and previous year.
 */
function getBirthdayDatesForYears(employeeBirthday: string, year: number) {
    const { month, day } = isoToZoned(employeeBirthday);

    const currentYearBirthday = new Temporal.PlainDateTime(year, month, day);
    const nextYearBirthday = new Temporal.PlainDateTime(year + 1, month, day);
    const prevYearBirthday = new Temporal.PlainDateTime(year - 1, month, day);

    return {
        currentYearBirthday,
        nextYearBirthday,
        prevYearBirthday,
    };
}
